import { CompetenceDownloadDto, ContentController, ControllerHelper, PlaylistAssignToPlaylistTrigger, PlaylistCertificateTrigger, PlaylistTrigger, PostController, PostDownloadDto } from 'collaboration-service';
import CancelWithConfirmation from 'components/CancelWithConfirmation';
import InfoBox from 'components/InfoBox/InfoBox';
import { Button, Modal } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { useImgI18N } from 'services/ImgI18N';
import PlaylistEditFinishedTrigger from './PlaylistEditFinishedTrigger';
import { PlaylistEditSequenceLinearSC as T } from './PlaylistEditSequenceLinearSC';
import SearchPosts, { SearchPostType } from './SearchPosts';
import { usePLEState } from './usePLEState';

export interface PlaylistEditAutomationProps {
    hasTriggersCallBack?: (hasTriggers: boolean) => void;
}




const PlaylistEditAutomation = (p: PlaylistEditAutomationProps) => {
    const { t } = useImgI18N("playlist");
    const [openSearch, setOpenSearch] = React.useState<{ kind: SearchPostType, callback: (post: PostDownloadDto) => void }>();
    const [posts, setPosts] = React.useState<PostDownloadDto[]>();
    const [postIds, setPostIds] = React.useState<string[]>();
    const [comps, setComps] = React.useState<CompetenceDownloadDto[]>();

    const s_finishedTriggers = usePLEState(x => x.finishedTriggers);

    // console.log('trigger => ', s_finishedTriggers.value);

    React.useEffect(() => {
        const ids = _.uniq(_.flatMap(s_finishedTriggers.value, t => {
            if (t.type === "PlaylistCertificateTrigger") {
                return (t as PlaylistCertificateTrigger).certificateIds ?? [];
            } else if (t.type === "PlaylistAssignToPlaylistTrigger") {
                const playlistId = (t as PlaylistAssignToPlaylistTrigger).playlistId;
                return playlistId ? [playlistId] : [];
            }
            return [];
        }));
        setPostIds(ids);
    }, [s_finishedTriggers.value]);

    React.useEffect(() => {
        const load = async () => {
            if (postIds !== undefined) {
                const curPostIds = _.map(posts, p => p.id);
                const toLoadIds = _.difference(postIds, curPostIds);
                if (toLoadIds.length === 0) {
                    setPosts(p => p === undefined ? [] : p);
                    return;
                }
                const t = _.map(toLoadIds, id => ControllerHelper.singleCall({ id }, PostController.GetSinglePost, true));
                const loadedPosts = await Promise.all(t);
                const comps = await ControllerHelper.singleCall({ grouptype: "CONFIGURATION" }, ContentController.GetCompetenceForAllGroupsByGroupType, true) as CompetenceDownloadDto[];
                setComps(comps);
                setPosts([...(posts ?? []), ...(_.compact(loadedPosts))]);
            }
        }
        load();
    }, [postIds, posts]);

    const search = React.useMemo(() => (kind: SearchPostType, callback: (post: PostDownloadDto) => void) => {
        setOpenSearch({ kind, callback });
    }, []);

    const addNewTrigger = React.useCallback(() => {
        const newTrigger: PlaylistCertificateTrigger = {
            type: "PlaylistCertificateTrigger",
            certificateIds: [],
        };
        s_finishedTriggers.setValue([...(s_finishedTriggers.value ?? []), newTrigger]);
    }, [s_finishedTriggers]);


    const onUpdateTrigger = React.useMemo(() => (index: number) => (trigger: PlaylistTrigger) => {
        const updatedTriggers = s_finishedTriggers.value ? [...s_finishedTriggers.value] : [];;
        updatedTriggers[index] = trigger;
        s_finishedTriggers.setValue(updatedTriggers);
    }, [s_finishedTriggers]);

    const onDeleteTrigger = React.useMemo(() => (index: number) => () => {
        const updatedTriggers = s_finishedTriggers.value ? [...s_finishedTriggers.value] : [];
        updatedTriggers.splice(index, 1);
        s_finishedTriggers.setValue(updatedTriggers);
    }, [s_finishedTriggers]);

    const onSearch = React.useMemo(() => (searchValue: PostDownloadDto) => {
        openSearch?.callback(searchValue);
        setOpenSearch(undefined);
    }, [openSearch]);
    const hasTriggers = React.useMemo(() => s_finishedTriggers.value && s_finishedTriggers.value.length > 0, [s_finishedTriggers]);

    React.useEffect(() => {
        p.hasTriggersCallBack?.(!!hasTriggers);
    }, [hasTriggers, p, p.hasTriggersCallBack]);
    const header = openSearch?.kind.toString() ?? "";

    return (
        <T.PEAContainer>
            <T.ImplContainerInfo>
                <InfoBox
                    noBold
                    noUppercase
                    noLetterSpacing
                    type={'info'}
                    icon="info"
                    content={hasTriggers ? t("to add more triggers, click the button on the right") : t("to add a trigger, click the button on the right")}
                    additionalButton={
                        <Button
                            kind={hasTriggers ? "secondary" : "primary"}
                            icon="add"
                            content={hasTriggers ? t("add more triggers") : t("add new trigger")}
                            onClick={addNewTrigger}
                            floated='right'
                        />
                    }
                />
            </T.ImplContainerInfo>
            {/* <div style={{ width: "100%", height: 50, marginTop: 10, paddingRight: 0 }}>
                <Button
                    kind={hasTriggers ? "secondary" : "primary"}
                    icon="add"
                    content={t("add new trigger")}
                    onClick={addNewTrigger}
                    floated='left'
                />
            </div> */}
            <div>
                {_.map(s_finishedTriggers.value, (trigger, i) =>
                    <T.PEATriggerContainer key={i}>
                        <PlaylistEditFinishedTrigger
                            posts={posts}
                            trigger={trigger}
                            competences={comps}
                            onUpdate={onUpdateTrigger(i)}
                            search={search}
                        />
                        <div style={{ padding: "10px 0px 0px 0px", }}>
                            <CancelWithConfirmation
                                cancel={onDeleteTrigger(i)}
                                icon="delete"
                                iconColor='@accentRed'
                                iconOnly
                                kind="transparentButton"
                                tooltipInitialButton={t("remove trigger")}
                                cancelText={t("cancel")}
                                confirmText={t("yes, remove trigger")}
                                floated='right'
                            />
                        </div>
                    </T.PEATriggerContainer>
                )}
            </div>

            {openSearch &&
                <Modal
                    active={true}
                    icon="search"
                    header={t("select {{for}}", { for: header })}
                    height="large"
                    width={"wide"}
                    footer={<Button kind="halfTransparentButton" floated='right' icon="times" content={t("cancel")} onClick={() => setOpenSearch(undefined)} />}
                >
                    <SearchPosts type={openSearch.kind!} onSearch={onSearch} />
                </Modal>
            }
        </T.PEAContainer >
    );
}

export default PlaylistEditAutomation;
